import React, { useState, useEffect, useCallback } from 'react';
import './ImageGalleryModal.css';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/800x600/333/666?text=Image+Not+Available';

function getGalleryUrls(download) {
  if (!download) return [];
  const galleryItems = Array.isArray(download.images) ? download.images : [];
  const nonCover = galleryItems
    .filter((i) => !i.isCover)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((i) => (typeof i === 'string' ? i : i?.url))
    .filter(Boolean);
  return nonCover;
}

export function ImageGalleryModal({ download, isOpen, onClose, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(false);

  const galleryUrls = getGalleryUrls(download);
  const hasImages = galleryUrls.length > 0;

  const handlePrevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : galleryUrls.length - 1));
  }, [galleryUrls.length]);

  const handleNextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev < galleryUrls.length - 1 ? prev + 1 : 0));
  }, [galleryUrls.length]);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNextImage();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleNextImage, handlePrevImage]);

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageLoadStart = () => {
    setIsLoading(true);
  };

  if (!isOpen || !hasImages) return null;

  const currentImage = galleryUrls[currentIndex];

  return (
    <div className="image-gallery-modal-overlay" onClick={onClose}>
      <div className="image-gallery-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          className="image-gallery-modal-close"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <i className="fa-solid fa-times"></i>
        </button>

        {/* Main image container */}
        <div className="image-gallery-modal-main">
          {galleryUrls.length > 1 && (
            <>
              <button
                className="image-gallery-modal-nav image-gallery-modal-nav--prev"
                onClick={handlePrevImage}
                aria-label="Previous image"
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <button
                className="image-gallery-modal-nav image-gallery-modal-nav--next"
                onClick={handleNextImage}
                aria-label="Next image"
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </>
          )}

          <div className="image-gallery-modal-image-container">
            {isLoading && (
              <div className="image-gallery-modal-loading">
                <i className="fa-solid fa-spinner fa-spin"></i>
              </div>
            )}
            <img
              src={currentImage}
              alt={`${download?.title || 'Download'} - ${currentIndex + 1}`}
              className="image-gallery-modal-image"
              onLoad={handleImageLoad}
              onLoadStart={handleImageLoadStart}
              onError={(e) => {
                setIsLoading(false);
                e.target.src = PLACEHOLDER_IMAGE;
              }}
            />
          </div>
        </div>

        {/* Image counter */}
        <div className="image-gallery-modal-counter">
          {currentIndex + 1} / {galleryUrls.length}
        </div>

        {/* Thumbnails */}
        {galleryUrls.length > 1 && (
          <div className="image-gallery-modal-thumbnails">
            <div className="image-gallery-modal-thumbnails-container">
              {galleryUrls.map((url, index) => (
                <button
                  key={index}
                  className={`image-gallery-modal-thumbnail ${
                    index === currentIndex ? 'active' : ''
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={url}
                    alt={`Thumbnail ${index + 1}`}
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_IMAGE;
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="image-gallery-modal-instructions">
          <span>Use arrow keys to navigate • Press ESC to close</span>
        </div>
      </div>
    </div>
  );
}